import {TerceroTelefono} from '../../../_models/tercero-telefono';
import {UnidadProductivaEconomicaVersion} from '../../../_models/terceros-unidades-productivas';
import {TerceroUPPasarelaAdjunto} from '../../../_models/tercero-up-pasarela-adjunto';

/**
 * Clase de color del chip de cada metdo de pasarela habilitado en el encabezado del titular.
 */
export const ADMSB_COLOR_POR_METODO: Record<string, string> = {
  'Link de Pagos': 'admsb-chip--naranja',
  'Tap on Phone': 'admsb-chip--azul',
  'Web Checkout': 'admsb-chip--violeta',
};

/**
 * Obtiene el número de celular a mostrar del titular: el principal habilitado y,
 * si ninguno está marcado como principal, el primer celular habilitado.
 *
 * @param telefonos Teléfonos registrados del tercero.
 * @returns Número de celular, o cadena vacía si el tercero no tiene celulares habilitados.
 */
export function extraerCelular(telefonos: TerceroTelefono[]): string {
  const celulares = (telefonos ?? []).filter(t => t.codigoTipoTelefono === 'CELU' && t.indicadorHabilitado);
  const principal = celulares.find(t => t.indicadorPrincipal) ?? celulares[0];
  return principal?.numeroTelefono ?? '';
}

/**
 * Deriva la ciudad de una dirección separada por comas, tomando los dos penúltimos
 * segmentos (ciudad y departamento) y descartando el país.
 *
 * @param direccion Dirección completa del establecimiento.
 * @returns Ciudad y departamento, o cadena vacía si la dirección tiene menos de 3 segmentos.
 */
export function extraerCiudad(direccion: string): string {
  const partes = (direccion ?? '').split(',').map(p => p.trim()).filter(Boolean);
  return partes.length >= 3 ? partes.slice(-3, -1).join(', ') : '';
}

/**
 * Concatena código y nombre de la actividad económica (Código CIIU).
 *
 * @param actividad Versión de actividad económica de la unidad productiva.
 * @returns Texto "codigo - nombre", o undefined si no se recibió actividad.
 */
export function formatearActividadEconomica(actividad?: UnidadProductivaEconomicaVersion): string | undefined {
  if (!actividad) {
    return undefined;
  }
  return [actividad.codigoActividadEconomica, actividad.actividadEconomica].filter(Boolean).join(' - ');
}

/**
 * Modificador de color del chip de estado de la tabla de tarifas.
 *
 * @param codigoEstado Código de lista del estado de la pasarela (ListasEstadosPasarela).
 * @returns Clase modificadora de `admsb-chip-estado`.
 */
export function claseEstado(codigoEstado: string): string {
  switch ((codigoEstado ?? '').toUpperCase()) {
    case 'ITM00': // ACTIVA
      return 'admsb-chip-estado--activo';
    case 'ITM002': // BLOQUEADO
      return 'admsb-chip-estado--bloqueado';
    case 'ITM003': // CANCELADO
      return 'admsb-chip-estado--cancelado';
    case 'ITM004': // SIN ACTIVAR
      return 'admsb-chip-estado--sin-activar';
    default:
      return 'admsb-chip-estado--gris';
  }
}

/**
 * Texto de estado del adjunto: "Cargado"/"Pendiente"/"Solicitado al cliente" según
 * idAdjunto e indicadorRetoma, seguido del tipo de vigencia.
 *
 * @param adjunto Adjunto de la pasarela del titular.
 * @returns Etiqueta de estado, con el tipo de vigencia añadido cuando existe.
 */
export function estadoAdjunto(adjunto: TerceroUPPasarelaAdjunto): string {
  const etiqueta = adjunto.idAdjunto ? 'Cargado' : (adjunto.indicadorRetoma ? 'Solicitado al cliente' : 'Pendiente');
  return adjunto.tipoVigencia ? `${etiqueta} - ${adjunto.tipoVigencia}` : etiqueta;
}

/**
 * Modificador de color del chip de estado de la tabla de adjuntos.
 *
 * @param adjunto Adjunto de la pasarela del titular.
 * @returns Clase modificadora de `admsb-chip-estado`.
 */
export function claseEstadoAdjunto(adjunto: TerceroUPPasarelaAdjunto): string {
  if (adjunto.idAdjunto) {
    return 'admsb-chip-estado--activo';
  }
  return adjunto.indicadorRetoma ? 'admsb-chip-estado--cancelado' : 'admsb-chip-estado--pendiente';
}

/**
 * Filtro local de los buscadores de las tablas: coincidencia parcial, sin distinguir
 * mayúsculas, sobre cualquiera de los campos indicados.
 *
 * @param items Registros a filtrar.
 * @param texto Texto escrito en el buscador.
 * @param campos Extractores del texto de cada columna buscable.
 * @returns Los registros que coinciden, o `items` sin filtrar si el texto está vacío.
 */
export function filtrarPorTexto<T>(items: T[], texto: string, campos: ((item: T) => string)[]): T[] {
  const busqueda = (texto ?? '').trim().toLowerCase();
  if (!busqueda) {
    return items;
  }
  return items.filter(item => campos.some(campo => (campo(item) ?? '').toLowerCase().includes(busqueda)));
}
